import { Link } from "react-router-dom";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import useSchedaArticolo from "../modificaProdotti/schedaArticolo/useSchedaArticolo";
import SchedaArticoloOverlay from "../modificaProdotti/schedaArticolo/SchedaArticoloOverlay";
import LinkArticolo from "../modificaProdotti/schedaArticolo/LinkArticolo";
import apiUrl from "../Apiurl";
import { BlockSkeleton } from "../interfaccia/loading";
import stilePulsanti from "../interfaccia/stilePulsanti";
import "../traduzioni/TraduzioniPage.css";
import "./AvanzamentoBackfillPage.css";
import { STATI } from "./statiBackfill";
import AttivitaBackfill from "./AttivitaBackfill";

// Avanzamento dei backfill: allineamento delle pubblicazioni VIG/BOT, recupero
// da BOT, immagini BOT e traduzioni. Solo lettura, aggiornamento ogni minuto.

const AGGIORNA_MS = 60_000;
const numero = (value) => (Number(value) || 0).toLocaleString("it-IT");
const data = (value) => (value ? new Date(value).toLocaleString("it-IT") : "—");
const percentuale = (parte, totale) => (totale > 0 ? Math.round((parte / totale) * 1000) / 10 : 0);

const ValoriIniziali = createContext(null);

function UltimoArticolo({ valore }) {
  return valore?.codVS ? <> (ultimo articolo rilevato: <LinkArticolo codVS={valore.codVS} title={data(valore.il)} />)</> : null;
}

function Barra({ chiave, etichetta, ariaLabel = etichetta, parte, totale, ultimo }) {
  const valoreIniziale = useContext(ValoriIniziali);
  const corrente = Number(parte) || 0;
  const differenza = corrente - valoreIniziale(chiave, corrente);
  const variazione = differenza > 0 ? `+${numero(differenza)}` : `−${numero(-differenza)}`;
  const descrizione = `${variazione} da quando hai aperto la pagina`;
  const valore = percentuale(parte, totale);
  return <div className="mb2">
    <div className="backfill-bar-label f6"><span>{etichetta}<UltimoArticolo valore={ultimo} /></span><span>{numero(parte)} / {numero(totale)} ({valore.toLocaleString("it-IT")}%)
      {differenza !== 0 && <strong className={differenza > 0 ? "backfill-variazione scrittaVerde ml2" : "backfill-variazione scrittaRosso ml2"} title={descrizione} aria-label={descrizione}>{variazione}</strong>}</span></div>
    <div className="ba bordoColAlt br2" style={{ height: ".6rem", overflow: "hidden" }}
      role="progressbar" aria-label={ariaLabel} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, Math.max(0, valore))}>
      <div className="h-100 sfondoColAlt" style={{ width: `${Math.min(100, Math.max(0, valore))}%`, background: "currentColor", opacity: 0.6 }} />
    </div>
  </div>;
}

function Stato({ attivo }) {
  return <span className="traduzioni-badge ba bordoColAlt">{attivo ? "Attivo" : "Spento"}</span>;
}

function Errore({ sezione }) {
  return sezione?.errore ? <p role="alert" className="f6">Lettura non riuscita: {sezione.errore}</p> : null;
}

export function riepilogoCheckpoint(valore) {
  const stati = valore?.stati || {};
  const lavorati = Object.values(stati).reduce((totale, n) => totale + (Number(n) || 0), 0);
  return { totale: lavorati + (Number(valore?.daAvviare) || 0), completati: Number(stati.DONE) || 0, stati, daAvviare: Number(valore?.daAvviare) || 0,
    // Dati minimi completi (DONE + "da rivedere" con soli facoltativi): assente nelle risposte precedenti.
    minimiCompleti: valore?.minimiCompleti === undefined ? null : Number(valore.minimiCompleti) || 0 };
}

function BarreCheckpointStagioni({ valore, chiave, immagini = false }) {
  return (valore.stagioni || []).map((stagione) => {
    const r = riepilogoCheckpoint(stagione);
    return <Barra key={stagione.stagione} chiave={`${chiave}:stagione:${stagione.stagione}`} etichetta={`Stagione ${stagione.stagione}`}
      parte={immagini ? r.totale - r.daAvviare : r.minimiCompleti ?? r.completati} totale={r.totale} />;
  });
}

function KpiQuota({ titolo, quota, chiave }) {
  const q = quota?.dati;
  return <section className="backfill-kpi traduzioni-card sfondoBiancoTema ba bordoColAlt br3 pa3" aria-label={titolo}>
    <h2 className="f5 mt0 mb2">{titolo}</h2>
    <Errore sezione={quota} />
    {!q && !quota?.errore && <p className="f6 mb0">Consumo non disponibile.</p>}
    {q && <>
      <p className="f6 mt0">{q.periodo} (mese UTC)</p>
      <p className="mv2"><strong className="f3">{numero(q.consumate)}</strong> <span className="f6">caratteri utilizzati</span></p>
      {q.tetto === null ? <p className="f6 mb0">Quota illimitata</p> : q.tetto === 0
        ? <p className="f6 mb0">Traduzioni disattivate: quota mensile 0.</p>
        : <><Barra chiave={chiave} etichetta="Quota utilizzata" parte={q.consumate} totale={q.tetto} />
          <p className="f6 mb0">{numero(q.residuo)} caratteri residui</p></>}
      {q.esaurito && <p role="status" className="f6 mb0">Canale esaurito presso il provider.</p>}
    </>}
  </section>;
}

function CardCheckpoint({ titolo, descrizione, valore, attivo, errore, chiave, ultimi }) {
  const r = riepilogoCheckpoint(valore);
  return <section className="traduzioni-card sfondoBiancoTema ba bordoColAlt br3 pa3" aria-label={titolo}>
    <h2 className="mt0">{titolo} <Stato attivo={attivo} /></h2>
    <p className="f6">{descrizione}</p>
    {errore && <Errore sezione={{ errore }} />}
    {valore && <>
      {r.minimiCompleti !== null && <Barra chiave={`${chiave}:minimi`} etichetta="Dati minimi completati" parte={r.minimiCompleti} totale={r.totale} />}
      <Barra chiave={`${chiave}:completati`} etichetta="Completati senza nulla da rivedere" parte={r.completati} totale={r.totale} />
      <div className="backfill-season-bars mt3"><BarreCheckpointStagioni chiave={chiave} valore={valore} /></div>
      <ul className="f6">
        <li>Da avviare: {numero(r.daAvviare)}</li>
        {Object.entries(r.stati).filter(([key]) => key !== "DONE").map(([key, n]) => <li key={key}>{STATI[key] || key}: {numero(n)}<UltimoArticolo valore={ultimi?.[key]} /></li>)}
        {valore.inScadenza > 0 && <li>Pronti per il prossimo ciclo: {numero(valore.inScadenza)}<UltimoArticolo valore={ultimi?.inScadenza} /></li>}
      </ul>
    </>}
  </section>;
}

function CardTraduzioni({ traduzioni, attivo, ultimi }) {
  const copertura = traduzioni?.copertura?.dati;
  return <section className="traduzioni-card sfondoBiancoTema ba bordoColAlt br3 pa3" aria-label="Traduzioni">
    <h2 className="mt0">Traduzioni <Stato attivo={attivo} /></h2>
    <p className="f6">Articoli pubblicati con stock web: il perimetro del backfill notturno.</p>
    <Errore sezione={traduzioni?.copertura} />
    {copertura && <div className="backfill-translation-sites">{["VIG", "BOT"].map((site) => {
      const s = copertura.siti?.[site];
      if (!s) return null;
      return <div key={site} className="mb3">
        <h3 className="f5 mb2">{site} · {numero(s.eleggibili)} articoli</h3>
        <Barra chiave={`traduzioni:${site}:domino`} ultimo={ultimi?.[site]?.tradotto} etichetta={`${site}: tradotti in Domino`} parte={s.tradottiDomino} totale={s.eleggibili} />
        <Barra chiave={`traduzioni:${site}:online`} etichetta={`${site}: online`} parte={s.online} totale={s.eleggibili} />
        <Barra chiave={`traduzioni:${site}:verificati`} ultimo={ultimi?.[site]?.verificato} etichetta={`${site}: verificati online`} parte={s.eleggibili - s.nonVerificati} totale={s.eleggibili} />
        <div className="backfill-translation-seasons mt3">{(copertura.stagioni || []).map((row) => {
          const st = row.siti?.[site];
          if (!st) return null;
          return <div key={row.stagione}>
            <h4 className="f6 mt0 mb2">Stagione {row.stagione}</h4>
            <Barra chiave={`traduzioni:${site}:${row.stagione}:domino`} etichetta="Tradotti in Domino" ariaLabel={`${site} stagione ${row.stagione}: tradotti in Domino`} parte={st.tradottiDomino} totale={st.eleggibili} />
            <Barra chiave={`traduzioni:${site}:${row.stagione}:online`} etichetta="Online" ariaLabel={`${site} stagione ${row.stagione}: online`} parte={st.online} totale={st.eleggibili} />
            <Barra chiave={`traduzioni:${site}:${row.stagione}:verificati`} etichetta="Verificati online" ariaLabel={`${site} stagione ${row.stagione}: verificati online`} parte={st.eleggibili - st.nonVerificati} totale={st.eleggibili} />
          </div>;
        })}</div>
        <div className="f6">{Object.entries(s.lingue || {}).map(([lingua, m]) =>
          <div key={lingua}>{lingua.toUpperCase()}: {numero(m.mancantiDomino)} da tradurre in Domino · {numero(m.mancantiOnline)} mancanti online
            · {numero(m.nonVerificati)} non verificati{m.erroriLettura ? ` · ${numero(m.erroriLettura)} errori di lettura` : ""}</div>)}</div>
      </div>;
    })}</div>}
    <ul className="f6">
      {traduzioni?.giaOnline?.dati !== null && traduzioni?.giaOnline?.dati !== undefined &&
        <li>Con testi già online da recuperare: {numero(traduzioni.giaOnline.dati)}</li>}
      {ultimi?.recuperate?.codVS && <li>Ultimo con traduzioni recuperate da BOT: <LinkArticolo codVS={ultimi.recuperate.codVS} title={data(ultimi.recuperate.il)} /></li>}
    </ul>
  </section>;
}

export default function AvanzamentoBackfillPage({ utente, setProductFunction, listaNomiProdotto }) {
  const scheda = useSchedaArticolo({ setProductFunction });
  const aperta = scheda.articolo !== null && scheda.articolo !== undefined;
  const iniziali = useRef(new Map());
  const apertaIl = useRef(new Date());
  const valoreIniziale = useCallback((chiave, parte) => {
    if (!iniziali.current.has(chiave)) iniziali.current.set(chiave, parte);
    return iniziali.current.get(chiave);
  }, []);
  const { getAccessTokenSilently } = useAuth0();
  const permessi = utente?.permessiAPI || {};
  const canRead = Boolean(permessi["read:database"] || permessi["read:dashboard_pubblicazioni"]);
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  const carica = useCallback(async (signal) => {
    const token = await getAccessTokenSilently();
    if (signal.aborted) return null;
    const response = await axios.get(`${apiUrl}/moda/backfill/avanzamento`, { headers: { Authorization: `Bearer ${token}` }, signal });
    if (!response.data?.success || !response.data.data) throw new Error("Risposta di avanzamento non valida.");
    return response.data.data;
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!canRead || aperta) return undefined;
    let active = true;
    const controller = new AbortController();
    carica(controller.signal).then((value) => { if (active) { setSnapshot(value); setError(""); } })
      .catch((err) => { if (active) setError(err?.response?.data?.error || err?.message || "Errore durante la lettura dell'avanzamento."); })
      .finally(() => { if (active) setLoading(false); });
    const timer = setTimeout(() => { if (active) setRefresh((value) => value + 1); }, AGGIORNA_MS);
    return () => { active = false; controller.abort(); clearTimeout(timer); };
  }, [canRead, carica, refresh, aperta]);

  if (!canRead) return <div className="pa3" role="alert">Per consultare l'avanzamento serve il permesso read:database o read:dashboard_pubblicazioni.</div>;
  const a = snapshot?.allineamento;
  const ultimi = snapshot?.ultimi?.errore ? null : snapshot?.ultimi?.dati;
  return <ValoriIniziali.Provider value={valoreIniziale}><div className="traduzioni-page pa3">
    <div className="traduzioni-heading"><h1>Avanzamento backfill</h1>
      <Link to="/avanzamentoBackfill/sandbox">Sandbox articolo</Link>
      <button className={stilePulsanti.buttonClassesWhite} onClick={() => setRefresh((value) => value + 1)} disabled={loading}>Aggiorna</button>
    </div>
    <p className="f6">Aggiornata ogni minuto{snapshot ? ` · ultima lettura ${data(snapshot.generatoIl)} · variazioni dalle ${apertaIl.current.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : ""}.</p>
    {error && <p role="alert">{error}</p>}
    {loading && !snapshot ? <BlockSkeleton height={320} /> : snapshot && <>
      <Errore sezione={snapshot.stagioni} />
      <div className="backfill-kpis mb3">
        <KpiQuota chiave="quota:google" titolo="Google Translate" quota={snapshot.traduzioni?.quote?.google} />
        <KpiQuota chiave="quota:azure" titolo="Microsoft Azure" quota={snapshot.traduzioni?.quote?.azure} />
      </div>
      <div className="traduzioni-cards">
        <CardCheckpoint chiave="VIG_TO_BOT" ultimi={ultimi?.minimi?.VIG_TO_BOT} titolo="Allineamento VIG → BOT" descrizione="Dati minimi e immagini dei pubblicati su VIG verso BOT."
          valore={a?.dati?.VIG_TO_BOT} attivo={snapshot.attivi?.allineamento} errore={a?.errore} />
        <CardCheckpoint chiave="BOT_TO_VIG" ultimi={ultimi?.minimi?.BOT_TO_VIG} titolo="Allineamento BOT → VIG" descrizione="Articoli non pubblicati su VIG importati da BOT."
          valore={a?.dati?.BOT_TO_VIG} attivo={snapshot.attivi?.allineamento} errore={a?.errore} />
        <CardCheckpoint chiave="BOT_RECOVERY" ultimi={ultimi?.minimi?.BOT_RECOVERY} titolo="Recupero da BOT" descrizione="Pubblicati su entrambi i siti: descIntro, categorie Bottero e traduzioni recuperate da BOT."
          valore={a?.dati?.BOT_RECOVERY} attivo={snapshot.attivi?.recuperoBot} errore={a?.errore} />
        <section className="traduzioni-card sfondoBiancoTema ba bordoColAlt br3 pa3" aria-label="Immagini BOT">
          <h2 className="mt0">Immagini BOT <Stato attivo={snapshot.attivi?.immagini} /></h2>
          <p className="f6">Articoli senza immagini in Domino: si cercano su BOT e si copiano su R2 e VIG (di notte).</p>
          <Errore sezione={snapshot.immagini} />
          {snapshot.immagini?.dati && (() => {
            const r = riepilogoCheckpoint(snapshot.immagini.dati);
            return <><Barra chiave="immagini:controllati" etichetta="Controllati" parte={r.totale - r.daAvviare} totale={r.totale} />
              <div className="backfill-season-bars mt3"><BarreCheckpointStagioni chiave="immagini" valore={snapshot.immagini.dati} immagini /></div>
              <ul className="f6"><li>Da controllare: {numero(r.daAvviare)}</li>
                {Object.entries(r.stati).map(([key, n]) => <li key={key}>{key}: {numero(n)}<UltimoArticolo valore={ultimi?.immagini?.[key]} /></li>)}</ul></>;
          })()}
        </section>
      </div>
      <div className="mt3"><CardTraduzioni ultimi={ultimi?.traduzioni} traduzioni={snapshot.traduzioni} attivo={snapshot.attivi?.traduzioni} /></div>

      <div className="traduzioni-table-scroll mt3">
        <table className="traduzioni-articles"><caption>Job di traduzione e verifica in corso</caption>
          <thead><tr><th>Job</th><th>Tipo</th><th>Richiesto da</th><th>Avanzamento</th><th>Ultimo passo</th></tr></thead>
          <tbody>{(snapshot.traduzioni?.job?.dati || []).map((job) => <tr key={job.job_id}>
            <td className="f7">{job.job_id}</td><td>{job.job_type === "verifica_traduzioni_online" ? "Verifica online" : "Traduzione"}</td>
            <td>{job.requested_by}</td>
            <td><Barra chiave={`job:${job.job_id}`} etichetta={`Job ${job.job_id}`} parte={job.processed_count} totale={job.total_items} /></td>
            <td className="f7">{job.last_step || job.status}</td>
          </tr>)}</tbody>
        </table>
        {!(snapshot.traduzioni?.job?.dati || []).length && <p className="f6">Nessun job in corso.</p>}
        <Errore sezione={snapshot.traduzioni?.job} />
      </div>

      <div className="traduzioni-table-scroll mt3">
        <table className="traduzioni-articles"><caption>Ultimo esito dei cron</caption>
          <thead><tr><th>Cron</th><th>Quando gira</th><th>Esito</th><th>Messaggio</th><th>Ultima esecuzione</th></tr></thead>
          <tbody>{(snapshot.cron?.dati || []).map((row) => <tr key={row.funzione}>
            <th scope="row">{row.label}</th><td className="f7">{row.scheduleLabel || "—"}</td><td>{row.status || "—"}</td>
            <td className="f7">{row.messaggio || "Mai eseguito"}</td><td className="f7">{data(row.dataOperazione)}</td>
          </tr>)}</tbody>
        </table>
        <Errore sezione={snapshot.cron} />
      </div>
      <AttivitaBackfill refresh={refresh} aperta={aperta} />
    </>}
    {aperta && <SchedaArticoloOverlay scheda={scheda} utente={utente} setProductFunction={setProductFunction} listaNomiProdotto={listaNomiProdotto} />}
  </div></ValoriIniziali.Provider>;
}
